/**
 * Client-side mirror of the backend `text_preview()` formula helper, used to
 * derive a memo's display name from its body while the server-computed
 * `ahoy_display_name` hasn't landed yet (optimistic create). The ahoy_note
 * formula is `ahoy_title or text_preview(ahoy_content, 80) or "Note"` — see
 * apps/crm_events/schemas/properties/note_properties.yaml — so the provisional
 * name matches what the server stamps a moment later.
 *
 * The two implementations are pinned to byte-identical output by the shared
 * fixture apps/crm_events/services/tests/fixtures/text_preview_cases.json, which drives
 * both this module's tests and test_formula_text_preview.py. Change one side
 * and the other side's test fails.
 */

import DOMPurify from 'dompurify';

/** Keep in lockstep with the max_chars literal in the ahoy_note formula. */
export const MEMO_DISPLAY_NAME_PREVIEW_CHARS = 80;

const PREVIEW_ELLIPSIS = '…';

/**
 * Code points Python's `str.isspace()` (hence `str.split()` / `str.strip()`)
 * treats as whitespace. The backend collapses a body with
 * `" ".join(text.split())` (apps/crm_events/core/text_preview.py), so the mirror
 * must split on exactly this set — NOT JavaScript's `\s`, which diverges at the
 * edges and silently made an optimistic name differ from the server's:
 *   - Python treats U+001C–U+001F (file/group/record/unit separators) and
 *     U+0085 (NEL) as whitespace; JS `\s` does not — `\s+` would leave them
 *     glued into a word while the server dropped them.
 *   - JS `\s` matches U+FEFF (BOM / ZWNBSP); Python's `str.isspace()` does not —
 *     `\s+` would split on it while the server kept it.
 * Kept as numeric code points (no raw whitespace chars in source — U+2028/U+2029
 * are line terminators that would break a regex/string literal); keep in
 * lockstep with the backend.
 */
const PLAIN_TEXT_WHITESPACE_CODE_POINTS = [
  0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x1c, 0x1d, 0x1e, 0x1f, 0x20, 0x85, 0xa0,
  0x1680, 0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007,
  0x2008, 0x2009, 0x200a, 0x2028, 0x2029, 0x202f, 0x205f, 0x3000,
] as const;

const PLAIN_TEXT_WHITESPACE = new RegExp(
  `[${PLAIN_TEXT_WHITESPACE_CODE_POINTS.map(
    cp => `\\u${cp.toString(16).padStart(4, '0')}`
  ).join('')}]+`,
  'g'
);

/**
 * Tags whose boundaries are a word break in the rendered document. Matches the
 * `_BLOCK_TAGS` set in apps/crm_events/core/text_preview.py.
 */
const BLOCK_TAG_PATTERN =
  /<\/?(?:article|blockquote|br|div|footer|h[1-6]|header|hr|li|ol|p|pre|section|table|td|th|tr|ul)\b[^>]*>/gi;

/**
 * Plain text of a rich-text body — editor HTML and `<Entity>` mention tags
 * stripped (their text kept), whitespace collapsed — cut to `maxChars` code
 * points. Returns `null` for an empty or tag-only body so callers keep falling
 * through, exactly like the formula's `or` chain.
 */
export function memoContentPreview(
  content: string | null | undefined,
  maxChars = MEMO_DISPLAY_NAME_PREVIEW_CHARS
): string | null {
  if (typeof content !== 'string' || !content) {
    return null;
  }

  // Block boundaries become whitespace before the tags are dropped, so
  // "<p>a</p><p>b</p>" reads "a b", not "ab". Keeping the tag itself ('$&')
  // preserves the document structure the parser needs — a bare `<td>` outside
  // a table is discarded by the tree builder, its surrounding spaces are not.
  const spaced = content.replace(BLOCK_TAG_PATTERN, ' $& ');

  // Sanitize before parsing so untrusted body HTML never reaches the HTML
  // parser as live markup — DOMPurify strips event handlers and dangerous
  // constructs, and is the barrier that keeps this off the "DOM text
  // reinterpreted as HTML" path. The parsed tree stays detached and we only
  // ever read its text, but sanitizing keeps the sink safe regardless.
  //
  // FORBID_CONTENTS is narrowed to script/style — the two tags whose text the
  // backend also drops (_SKIPPED_TAGS in apps/crm_events/core/text_preview.py;
  // `code` is a kept tag stripped below). DOMPurify's default FORBID_CONTENTS
  // additionally discards the *text* of embed/metadata tags like `<iframe>`
  // and `<title>`, which Python's HTMLParser keeps — that divergence made an
  // optimistic name differ from the server's. The dangerous *elements* are
  // still removed either way (only their inert text is preserved), and we only
  // ever read textContent, so narrowing the set does not weaken the sink.
  const doc = new DOMParser().parseFromString(
    DOMPurify.sanitize(spaced, { FORBID_CONTENTS: ['script', 'style'] }),
    'text/html'
  );
  // script/style/code content is code, not prose, and must never surface in a
  // name. Mirrors _SKIPPED_TAGS in apps/crm_events/core/text_preview.py.
  doc.querySelectorAll('script, style, code').forEach(node => {
    node.remove();
  });

  // Collapse runs of whitespace to a single space using Python's whitespace
  // set (see PLAIN_TEXT_WHITESPACE) rather than JS `\s`, so the split matches
  // the backend's `text.split()` for every code point.
  const text = (doc.body.textContent ?? '')
    .split(PLAIN_TEXT_WHITESPACE)
    .filter(Boolean)
    .join(' ');
  if (!text) {
    return null;
  }

  // Cut by code points, not UTF-16 units, so an emoji straddling the boundary
  // is kept whole rather than sliced into a lone surrogate (matches Python).
  const codePoints = Array.from(text);
  if (codePoints.length <= maxChars) {
    return text;
  }
  // Strip only a trailing ASCII space — the one whitespace the collapse above
  // can leave at the cut — to mirror Python's `rstrip()`. NOT `trimEnd()`: JS
  // treats U+FEFF as whitespace and would drop it here, but the collapse
  // deliberately PRESERVES U+FEFF (Python's `str.isspace()` excludes it, so the
  // backend's `rstrip()` keeps it too). Using `trimEnd()` made an optimistic
  // name differ from the server's whenever a U+FEFF landed at the cut.
  return (
    codePoints.slice(0, maxChars).join('').replace(/ +$/, '') + PREVIEW_ELLIPSIS
  );
}
